import * as Common from "@sp-crm/core";
import { CalendarDate } from "@sp-crm/core";
import { trpcClient } from "components/rpc/trpc";
import { CalendarDateInput } from "components/ui/calendar-date";
import { fancyAlert } from "components/ui/fancy-alert";
import { PrimaryButton, PrimaryCompactButton } from "components/ui/primary-button";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useMemo, useState } from "react";
import { useTenantSettings } from "store/selectors/settings";
import { buildDurationOptions } from "./time-entry-utils";

interface ManualTimeEntryFormProps {
    clientId: Common.PlacementId;
    onSaved: () => void;
    onCancel?: () => void;
    compact?: boolean;
}

function startTimeForEntry(date: CalendarDate): Date {
    const now = new Date();
    const today = CalendarDate.fromLocalDate(now);
    if (date.equals(today)) {
        return now;
    }
    return date.withTime(12, 0, 0);
}

export const ManualTimeEntryForm: React.FC<ManualTimeEntryFormProps> = ({
    clientId,
    onSaved,
    onCancel,
    compact,
}) => {
    const { settings } = useTenantSettings();
    const increment = settings.timeTrackingIncrementMinutes || 15;
    const options = useMemo(() => buildDurationOptions(increment), [increment]);
    const [durationMinutes, setDurationMinutes] = useState(increment);
    const [note, setNote] = useState("");
    const [date, setDate] = useState<CalendarDate>(() => CalendarDate.todayNoTime);
    const [saving, setSaving] = useState(false);

    const handleSubmit = useCallback(async () => {
        if (!note.trim()) return;
        setSaving(true);
        try {
            await trpcClient.timeEntries.create.mutate({
                clientId,
                startTime: startTimeForEntry(date).toISOString(),
                endTime: null,
                durationMinutes,
                note: note.trim(),
                isManual: true,
            });
            setNote("");
            setDurationMinutes(increment);
            setDate(CalendarDate.todayNoTime);
            onSaved();
        } catch {
            await fancyAlert("Error", "Failed to save time entry. Please try again.");
        } finally {
            setSaving(false);
        }
    }, [clientId, date, durationMinutes, note, increment, onSaved]);

    const handleDateChange = useCallback((newValue: CalendarDate | null) => {
        setDate(newValue ?? CalendarDate.todayNoTime);
    }, []);

    return (
        <div className="space-y-4">
            <CalendarDateInput
                label="Date"
                textSize="sm"
                value={date}
                maxDate={CalendarDate.todayNoTime}
                onChange={handleDateChange}
            />
            <Select
                label="Duration"
                textSize="sm"
                value={durationMinutes}
                onChange={e => setDurationMinutes(Number(e.target.value))}>
                {options.map(opt => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </Select>
            <TextArea
                label="What was done"
                textSize="sm"
                rows={3}
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Describe what was done..."
            />
            <div className="flex gap-2">
                {compact ? (
                    <PrimaryCompactButton
                        onClick={handleSubmit}
                        disabled={!note.trim() || saving}>
                        {saving ? "Saving..." : "Save"}
                    </PrimaryCompactButton>
                ) : (
                    <PrimaryButton
                        onClick={handleSubmit}
                        disabled={!note.trim() || saving}>
                        {saving ? "Saving..." : "Save"}
                    </PrimaryButton>
                )}
                {onCancel ? (
                    compact ? (
                        <SecondaryCompactButton onClick={onCancel} disabled={saving}>
                            Cancel
                        </SecondaryCompactButton>
                    ) : (
                        <SecondaryButton onClick={onCancel} disabled={saving}>
                            Cancel
                        </SecondaryButton>
                    )
                ) : null}
            </div>
        </div>
    );
};
